/* eslint-disable react-hooks/exhaustive-deps */
import _ from "lodash";
import { useEffect, useMemo, useState } from "react";
import useDocumentTitle from "../../../utils/useDocumentTitle";
import styles from "./SessionsOverview.module.scss";

import PageContent from "../../../components/PageContent";
import Section from "../../../components/Section";
import SectionTitle from "../../../components/SectionTitle";
import { DashboardLayout } from "../../../layouts/DashboardLayout";

import { MetricsWrapper } from "../../../components/Metric";

import { useNotification } from "../../../context/notification";
import { apiRequest } from "../../../utils/api";

import { Button, Collapse, Table, TableBody, TableCell, TableRow, TextField } from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";
import { FilterListOutlined } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import Autocomplete from "../../../components/Autocomplete";
import CampaignFilter from "../../../components/CampaignFilter";
import DataTable from "../../../components/DataTable";
import DateRangePicker from "../../../components/DateRangePicker";
import GetAllCountries from "../../../components/GetAllCountries";
import Search from "../../../components/Search";
import { MetricV2 } from "../../../components/Metric/index2";
import { Framework } from "../../../config/Config";
import { getLocalValue } from "../../../utils/auth";
import { formattedDateString, utcToLocal } from "../../../utils/datetime";
import { loadFilterState } from "../../logs/useLogsFilters";

const STORAGE_KEY = "sessionsSupport_filterState";

async function supportAdminAllSessions({
  campaignId,
  countryId,
  page,
  limit,
  columnName,
  order,
  startDate,
  endDate,
  type,
  subject,
  coach,
  coachee,
  searchKey,
}) {
  const { data } = await apiRequest.get("/api/supportAdminAllSessions", {
    params: {
      page,
      limit,
      columnName: columnName,
      order,
      campaign_id: campaignId,
      country_id: countryId,
      start_date: startDate,
      end_date: endDate,
      type,
      subject,
      coach_name: coach,
      coachee_name: coachee,
      search: searchKey,
    }
  })
  return data;
}
const columns = [
  {
    id: "session_ongoing_upcoming_date",
    label: "Upcoming session",
    sortable: true,
    render: (value) => {
      return utcToLocal(value).format("DD MMM YYYY")
    }
  },
  {
    name: "progress",
    label: "Session Date",
    sortable: false,
    render: (_, row) => {
      return utcToLocal(row.progress.session_datetime).format("DD MMM YYYY")
    }
  },
  {
    id: "session_id",
    label: "Session ID",
    sortable: false
  },
  {
    id: "progress",
    label: "Time",
    sortable: false,
    render: (_, row) => {
      return utcToLocal(row.progress.session_datetime).format("hh:mm A")
    }
  },
  {
    id: "session_ongoing_upcoming_type",
    label: "Type", sortable: false
  },
  {
    id: "subject",
    label: "Subjects(s)",
    sortable: false
  },
  {
    id: "coach_fullname",
    label: "Coach",
    sortable: false
  },
  {
    id: "coach_employee_id",
    label: "Coach Employee ID",
    sortable: false
  },
  {
    id: "coachee_fullname",
    label: "Coachee",
    sortable: false
  },
  {
    id: "coachee_employee_id",
    label: "Coachee Employee ID",
    sortable: false
  },
  {
    id: "session_status",
    label: "Status",
    sortable: false,
    render: (val) => {
      return (
        <span className={styles.status}>{val}</span>
      )
    }
  },
]

export function SessionsSupportOverview(props) {
  useDocumentTitle(props.title);
  const { t } = useTranslation();
  const { addNotification } = useNotification();
  const [selectedCountry, setSelectedCountry] = useState("all");
  const [selectedCampaign, setSelectedCampaign] = useState("all");
  const [tableData, setTableData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [_saved] = useState(() => loadFilterState(STORAGE_KEY));
  const [showFilter, setShowFilter] = useState(_saved.showFilter ?? false);
  const [selectedDateRange, setSelectedDateRange] = useState(
    _saved.selectedDateRange ?? { startDate: null, endDate: null }
  );
  const [selectedType, setSelectedType] = useState(_saved.selectedType);
  const [selectedSubject, setSelectedSubject] = useState(_saved.selectedSubject);
  const [selectedCoachNames, setSelectedCoachNames] = useState(_saved.selectedCoachNames);
  const [selectedNames, setSelectedNames] = useState(_saved.selectedNames);
  const [searchKey, setSearchKey] = useState(_saved.searchKey ?? "");
  const [count, setCount] = useState(0)

  const [subjectList, setSubjectList] = useState([]);
  const [coachNames, setCoachNames] = useState([]);
  const [coacheeNames, setCoacheeNames] = useState([]);

  const userRole = getLocalValue("u_coaching_role_db");

  const handleDateFilter = val => {
    setSelectedDateRange(val);
  };

  const filters = useMemo(
    () => ({
      startDate: formattedDateString(selectedDateRange.startDate),
      endDate: formattedDateString(selectedDateRange.endDate),
      type: selectedType?.type ?? null,
      subject: selectedSubject?.sub_subject ?? null,
      coach: selectedCoachNames?.coach_name ?? null,
      coachee: selectedNames?.name ?? null,
      countryId: selectedCountry,
      campaignId: selectedCampaign,
      searchKey: searchKey || null,
    }),
    [selectedDateRange, selectedType, selectedSubject, selectedCoachNames, selectedNames, selectedCountry, selectedCampaign, searchKey]
  );

  // persist filter state so it survives navigating into a session and back
  useEffect(() => {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        showFilter,
        selectedDateRange,
        selectedType,
        selectedSubject,
        selectedCoachNames,
        selectedNames,
        searchKey,
      })
    );
  }, [
    showFilter,
    selectedDateRange,
    selectedType,
    selectedSubject,
    selectedCoachNames,
    selectedNames,
    searchKey,
  ]);

  useEffect(() => {
    fetchData();
  }, [selectedCountry, selectedCampaign]);

  useEffect(() => {
    if (selectedCountry && selectedCampaign) {
      getSubjectList();
      getCoachList();
      getCoacheeList();
    }
  }, [selectedCountry, selectedCampaign]);

  // fetch from getSessionDetails api

  async function fetchData() {
    setIsLoading(true);
    const response = await apiRequest.get(
      `/api/supportAdminAllSessions?country_id=${selectedCountry}&campaign_id=${selectedCampaign}`
    );

    if (response.status === 200) {
      const data = response.data.data;
      setTableData(data);
      setCount(response.data.total)
    } else {
      addNotification(
        t("error.couldNotGetSession") + ` (Status code: ${response.status})`,
        "error"
      );
    }
    setIsLoading(false);
  }

  async function getSubjectList() {
    const response = await apiRequest.get(
      `api/getSubjectForLogFilter?user_role=${userRole}&campaign_id=${selectedCampaign}&country_id=${selectedCountry}`
    );
    if (response.status === 200) {
      setSubjectList(response.data.data);
    } else {
      addNotification(
        t("error.couldNotGetSession") + ` (Status code: ${response.status})`,
        "error"
      );
    }
  }

  async function getCoachList() {
    const response = await apiRequest.get(
      `api/getCoachList?user_role=${userRole}&campaign_id=${selectedCampaign}&country_id=${selectedCountry}`
    );
    if (response.status === 200) {
      setCoachNames(response.data.data);
    } else {
      addNotification(
        t("error.couldNotGetSession") + ` (Status code: ${response.status})`,
        "error"
      );
    }
  }

  async function getCoacheeList() {
    const response = await apiRequest.get(
      `api/getCoacheeLists?user_role=${userRole}&campaign_id=${selectedCampaign}&country_id=${selectedCountry}`
    );
    if (response.status === 200) {
      setCoacheeNames(response.data.data);
    } else {
      addNotification(
        t("error.couldNotGetSession") + ` (Status code: ${response.status})`,
        "error"
      );
    }
  }

  const uniqueSubjectList = Array.from(
    new Set(subjectList.map(item => item.type))
  ).map(type => subjectList.find(item => item.type === type));

  const renderExpandedContent = (row) => {
    const isPDSession = row.framework === Framework.PD;
    const progress = row.progress;

    const sessionDetailObl = {}

    if (progress.session_datetime) {
      sessionDetailObl.sessionLabel = "Coaching";
      sessionDetailObl.sessionDate = utcToLocal(
        progress.session_datetime
      ).format("DD MMM YYYY");
      sessionDetailObl.sessionTime = utcToLocal(
        progress.session_datetime
      ).format("h:mm A");
      sessionDetailObl.sessionCompleted = progress.session_datetime_completed
        ? true
        : false;
    }

    if (progress.review_datetime) {
      sessionDetailObl.reviewLabel = "Review";
      sessionDetailObl.reviewDate = utcToLocal(
        progress.review_datetime
      ).format("DD MMM YYYY");
      sessionDetailObl.reviewTime = utcToLocal(
        progress.review_datetime
      ).format("h:mm A");
      sessionDetailObl.reviewCompleted = progress.review_datetime_completed
        ? true
        : false;
    }

    const to_be_ensembled = _.pickBy(progress, (value, key) =>
      _.includes(key, "checkpoint_date")
    );

    let sessionCheckPoints = [];
    if (!_.isEmpty(to_be_ensembled)) {
      for (let i = 0; i < Object.keys(to_be_ensembled).length; i++) {
        let checkpoint = {};
        checkpoint.label = "Checkpoint " + (i + 1);
        if (to_be_ensembled["checkpoint_date_" + (i + 1)]) {
          checkpoint.date = to_be_ensembled["checkpoint_date_" + (i + 1)];
        }
        if (to_be_ensembled["checkpoint_date_" + (i + 1) + "_completed"]) {
          checkpoint.completed =
            to_be_ensembled["checkpoint_date_" + (i + 1) + "_completed"];
        }
        sessionCheckPoints.push(checkpoint);
      }
      const filteredCheckPoint = _.filter(sessionCheckPoints, function (obj) {
        if (obj.date) return obj;
      });
      sessionDetailObl.checkPoints = [...filteredCheckPoint];
    }

    return <Collapse in={true}
      timeout="auto"
      style={{
        padding: "16px",
        overflow: "hidden",
        marginTop: "-22px",
      }}
      unmountOnExit>
      <Table size="small" aria-label="purchases">
        <TableBody>
          <TableRow>
            <TableCell
              className={`${styles.tableDetailsColumn} ${styles.lineIndication}`}
              style={{ width: "15%" }}
            >
              {!sessionDetailObl.sessionCompleted && (
                <FiberManualRecordIcon className={styles.nonCompleted} />
              )}
              {sessionDetailObl.sessionCompleted && (
                <CheckCircleIcon className={styles.completed} />
              )}
              {sessionDetailObl.sessionDate}
            </TableCell>
            <TableCell
              className={styles.tableDetailsColumn}
              style={{ width: "10%" }}
            >
              {sessionDetailObl.sessionTime}
            </TableCell>
            <TableCell
              className={styles.tableDetailsColumn}
              style={{ width: "10%" }}
            >
              {sessionDetailObl.sessionLabel}
            </TableCell>
            <TableCell
              className={styles.tableDetailsColumn}
              style={{ width: "65%" }}
            ></TableCell>
          </TableRow>
        </TableBody>
        {sessionDetailObl.checkPoints &&
          sessionDetailObl.checkPoints.map((checkPoint, index) => (
            <TableRow key={index} className={styles.tableDetailsRow}>
              <TableCell
                className={`${styles.tableDetailsColumn} ${styles.lineIndication}`}
                style={{ width: "15%" }}
              >
                {!checkPoint.completed && (
                  <FiberManualRecordIcon className={styles.nonCompleted} />
                )}
                {checkPoint.completed && (
                  <CheckCircleIcon className={styles.completed} />
                )}
                {utcToLocal(checkPoint.date).format("DD MMM YYYY")}
              </TableCell>
              <TableCell
                className={styles.tableDetailsColumn}
                style={{ width: "10%" }}
              >
                -
              </TableCell>
              <TableCell
                className={styles.tableDetailsColumn}
                style={{ width: "10%" }}
              >
                {checkPoint.label}
              </TableCell>
              <TableCell
                className={styles.tableDetailsColumn}
                style={{ width: "65%" }}
              ></TableCell>
            </TableRow>
          ))}
        {!isPDSession && (
          <TableRow className={styles.tableDetailsRow}>
            <TableCell
              className={`${styles.tableDetailsColumn} ${styles.lineIndication}`}
              style={{ width: "15%" }}
            >
              {!sessionDetailObl.reviewCompleted && (
                <FiberManualRecordIcon className={styles.nonCompleted} />
              )}
              {sessionDetailObl.reviewCompleted && (
                <CheckCircleIcon className={styles.completed} />
              )}
              {sessionDetailObl.reviewDate}
            </TableCell>
            <TableCell
              className={styles.tableDetailsColumn}
              style={{ width: "10%" }}
            >
              {sessionDetailObl.reviewTime}
            </TableCell>
            <TableCell
              className={styles.tableDetailsColumn}
              style={{ width: "10%" }}
            >
              {sessionDetailObl.reviewLabel}
            </TableCell>
            <TableCell
              className={styles.tableDetailsColumn}
              style={{ width: "65%" }}
            ></TableCell>
          </TableRow>
        )}
      </Table>
    </Collapse>
  }

  return (
    <DashboardLayout>
      <div className={styles.pageWrapper}>
        <PageContent>
          <Section>
            <SectionTitle>Sessions</SectionTitle>

            <div className={styles.toolBar}>
              <GetAllCountries
                countryChange={val => {
                  setSelectedCountry(val);
                }}
              />
              <CampaignFilter
                handleCampaignChange={val => {
                  setSelectedCampaign(val);
                }}
              />
            </div>

            <MetricsWrapper>
              <MetricV2
                label="Total sessions pending"
                count={count}
              />
            </MetricsWrapper>

            <div className={styles.filterWrapper}>
              <div className={styles.search}>
                <Search
                  handleSearch={val => {
                    setSearchKey(val);
                  }}
                  searchInputValue={searchKey}
                  placeholder="Search by Session ID"
                />
              </div>
              <div
                onClick={() => {
                  setShowFilter(!showFilter);
                }}
                className={styles.filterButton}
              >
                {t("logs.filter")}
                <FilterListOutlined />
              </div>
              {showFilter && (
                <div className={styles.filterRow}>
                  <div className={styles.dateRange}>
                    <DateRangePicker
                      selectedDateRange={selectedDateRange}
                      handleDateChange={handleDateFilter}
                      disableFuture
                    />
                  </div>
                  <Autocomplete
                    value={selectedType}
                    defaultValue={selectedType}
                    onChange={(event, newValue) => {
                      setSelectedType(newValue);
                    }}
                    options={uniqueSubjectList}
                    getOptionLabel={option => option.type}
                    style={{ width: 140 }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder={t("logs.type")}
                        variant="outlined"
                      />
                    )}
                  />
                  <Autocomplete
                    value={selectedSubject}
                    defaultValue={selectedSubject}
                    onChange={(event, newValue) => {
                      setSelectedSubject(newValue);
                    }}
                    options={subjectList}
                    groupBy={option => option.subject}
                    getOptionLabel={option => option.sub_subject}
                    style={{ width: 240 }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder={t("logs.subjects")}
                        variant="outlined"
                      />
                    )}
                  />
                  <Autocomplete
                    value={selectedCoachNames}
                    defaultValue={selectedCoachNames}
                    onChange={(event, newValue) => {
                      setSelectedCoachNames(newValue);
                    }}
                    options={coachNames}
                    getOptionLabel={option => option.coach_name}
                    style={{ width: 150 }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder={t("logs.coach")}
                        variant="outlined"
                      />
                    )}
                  />
                  <Autocomplete
                    value={selectedNames}
                    defaultValue={selectedNames}
                    onChange={(event, newValue) => {
                      setSelectedNames(newValue);
                    }}
                    options={coacheeNames}
                    getOptionLabel={option => option.name}
                    style={{ width: 150 }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder={t("logs.names")}
                        variant="outlined"
                      />
                    )}
                  />
                  <div>
                    <Button
                      className={styles.clearButton}
                      onClick={() => {
                        setSelectedDateRange({ startDate: null, endDate: null });
                        setSelectedType(undefined);
                        setSelectedSubject(undefined);
                        setSelectedCoachNames(undefined);
                        setSelectedNames(undefined);
                        setSearchKey("");
                        setShowFilter(false);
                      }}
                    >
                      {t("logs.clearAll")}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {tableData && (
              <DataTable
                columns={columns}
                fetchData={supportAdminAllSessions}
                filters={filters}
                defaultOrderBy="session_ongoing_upcoming_date"
                defaultOrder="desc"
                expandable={true}
                renderExpandedContent={renderExpandedContent}
                onRowClick={true}
                storageKey="sessionsSupport"
              />
            )}
          </Section>
        </PageContent>
      </div>
    </DashboardLayout>
  );
}
